import HttpClient from "@/api/HttpClient";
import { APIHosts, ApiReduxHelpers } from "@/services/reduxHelpers";

type CompletedPart = { partNumber: number; etag: string };

export interface CompleteMultipartPayload {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  bucketName?: string;
  objectKey: string;
}

export interface UploadConfirmPayload {
  fileId: string;
  metadata: {
    filename: string;
    owner?: string;
    tags?: string[];
    description?: string;
    parentId?: string;
  };
  isOrganization?: boolean;
}

class UploadApiController extends ApiReduxHelpers {
  constructor() {
    super();
  }
  ApiHost = APIHosts.Upload;
  ApiUser = APIHosts.Authen;

  // Helper để lấy companyId từ localStorage hoặc context
  private getCompanyId(): number {
    return Number(localStorage.getItem("companyId")) || 1;
  }

  public Get = {
    getConfig: () =>
      HttpClient.get(`${this.getHost(this.ApiHost)}/api/Upload/config`),

    getFullPermission: (params?: {
      page?: number;
      pageSize?: number;
      type?: string;
    }) => {
      const base = `${this.getHost(this.ApiHost)}/Permission`;
      if (!params) return HttpClient.get(base);
      const q: string[] = [];
      if (params.page !== undefined) q.push(`page=${params.page}`);
      if (params.pageSize !== undefined) q.push(`pageSize=${params.pageSize}`);
      if (params.type !== undefined) q.push(`type=${params.type}`);
      const url = q.length ? `${base}?${q.join("&")}` : base;
      return HttpClient.get(url);
    },

    getDocumentUsersPermissions: (documentId: string | number) => {
      const base = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/document/${documentId}/users-permissions`;
      return HttpClient.get(base);
    },

    getFolderUsersPermissions: (folderId: string | number) => {
      const base = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/folder/${folderId}/users-permissions`;
      return HttpClient.get(base);
    },

    getOrganizationUsers: (params: { orgId: string; page: number; pageSize: number }) => {
      const { orgId, page, pageSize } = params;
      const base = `${this.getHost(
        this.ApiUser
      )}/license_manager/organizations/${orgId}/users?page=${page}&pageSize=${pageSize}`;

      return HttpClient.get(base);
    },

    getRoles: () => {
      const base = `${this.getHost(this.ApiHost)}/Role`;
      return HttpClient.get(base);
    },

    // Lấy thông tin sử dụng dung lượng của công ty
    getCompanyStorageUsage: (companyId: string, labelId: string) =>
      HttpClient.get(
        `${this.getHost(this.ApiHost)}/Drive/team/${labelId}/company/${companyId}`
      ),

    getFolderUserPermissions: (
      folderId: string | number,
      userId: string | number
    ) => {
      const base = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/folder/${folderId}/user/${userId}/permissions`;
      return HttpClient.get(base);
    },

    getFileUserPermissions: (
      documentId: string | number,
      userId: string | number
    ) => {
      const base = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/document/${documentId}/user/${userId}/permissions`;
      return HttpClient.get(base);
    },

    getFolderUsersPermissionsRole: (folderId: string | number) => {
      const base = `${this.getHost(
        this.ApiHost
      )}/RolePermission/folder/${folderId}/users-permissions`;
      return HttpClient.get(base);
    },
  };

  public Post = {
    // NEW: Khởi tạo multipart upload
    updateUserStorageUsage: (payload: {
      bytes: number;
      documentCount: number;
      isAdd: boolean;
    }) => {
      const url = `${this.getHost(this.ApiHost)}/UserStorageUsage/update`;
      return HttpClient.post(url, payload);
    },

    startMultipart: (
      file: File,
      projectName: string,
      version?: string,
      existingFileId?: string,
      parentId?: string,
      size?: number,
      companyId?: string
    ) => {
      const effectiveCompanyId = companyId || this.getCompanyId();
      const body: any = {
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        project: projectName,
        parentId: parentId,
      };

      // Chỉ thêm version nếu nó tồn tại
      if (version) {
        body.version = version;
      }

      // Thêm existingFileId nếu có (cho file đã tồn tại)
      if (existingFileId) {
        body.fileId = existingFileId;
      }

      // Build URL with companyId and optional size
      let url = `${this.getHost(
        this.ApiHost
      )}/api/upload/multipart/init?companyId=${effectiveCompanyId}`;
      
      if (size !== undefined) {
        url += `&size=${size}`;
      }

      return HttpClient.post(url, body);
    },

    startSinglepart: (
      file: File,
      projectName: string,
      version?: string,
      existingFileId?: string,
      parentId?: string,
      companyId?: string
    ) => {

      const body: any = {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || "application/octet-stream",
        project: projectName,
        parentId: parentId,
      };

      // Chỉ thêm version nếu tồn tại
      if (version) {
        body.version = version;
      }

      // File đã tồn tại (overwrite / new version)
      if (existingFileId) {
        console.warn("Starting single-part upload for existing fileId:", existingFileId);
        body.fileId = existingFileId;
      }

      const url = `${this.getHost(
        this.ApiHost
      )}/api/upload/single/init?companyId=${companyId}`;

      return HttpClient.post(url, body);
    },

    // NEW: Ký presigned URL cho từng part
    signPart: (uploadId: string, objectKey: string, partNumber: number) => {
      const body = {
        uploadId,
        objectKey,
        partNumber,
      };
      return HttpClient.post(
        `${this.getHost(this.ApiHost)}/api/upload/multipart/presign`,
        body
      );
    },

    // NEW: Hoàn tất multipart upload
    completeMultipart: (
      fileId: string,
      uploadId: string,
      objectKey: string,
      parts: CompletedPart[]
    ) => {
      const body = {
        fileId,
        uploadId,
        objectKey,
        parts,
      };
      return HttpClient.post(
        `${this.getHost(this.ApiHost)}/api/upload/multipart/complete`,
        body
      );
    },

    // NEW: Tạo record trên database sau khi upload hoàn tất
    confirmUpload: (payload: UploadConfirmPayload, companyId: string, orgId?: string) => {
      // isOrganization will be passed from the component based on currentWorkspace
      const bodyData = {
        ...payload,
      };

      // Build URL with companyId and optional teamId
      let url = `${this.getHost(
        this.ApiHost
      )}/api/upload/confirm?companyId=${companyId}`;

      if (orgId) {
        url += `&orgId=${orgId}`;
      }

      return HttpClient.post(url, bodyData);
    },

    // Giữ lại versionComplete cho các luồng cũ (nếu cần)
    versionComplete: (fileId: number, body: CompleteMultipartPayload) => {
      return HttpClient.post(
        `${this.getHost(this.ApiHost)}/api/files/${fileId}/versions/complete`,
        body
      );
    },
    mappingPermission: (params: {
      userId: string;
      permissionIds: string[];
      fileIds?: string[];
      labelIds?: string[];
    }) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/multi/mappingPermission`;

      return HttpClient.post(url, params);
    },

    // NEW: Role mapping for label
    roleMappingLabel: (body: {
      labelId: string;
      roleId: string;
      userId: string;
    }) => {
      const url = `${this.getHost(this.ApiHost)}/RoleUser/assign`;
      return HttpClient.post(url, body);
    },
  };

  public Put = {
    // Add permission to existing mapping
    addPermission: (id: string, permissionId: string) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/addPermission?id=${id}&permissionId=${permissionId}`;
      return HttpClient.put(url);
    },

    // Delete permission from existing mapping
    delPermission: (id: string, permissionId: string) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/MappingPermission/delPermission?id=${id}&permissionId=${permissionId}`;
      return HttpClient.put(url);
    },
    renameLabel: (labelId: string, newName: string) => {
      const url = `${this.getHost(this.ApiHost)}/Label/${labelId}`;
      const body = {
        name: newName,
      };
      return HttpClient.put(url, body);
    },
  };

  public Delete = {
    // NEW: Hủy multipart upload - accepts array of uploads
    abortMultipart: (uploads: Array<{fileId: string, uploadId: string, objectKey: string}>) => {
      const body = {
        uploads,
      };
      return HttpClient.post(
        `${this.getHost(this.ApiHost)}/api/Upload/abort`,
        body
      );
    },

    // Revoke ownership - Thu hồi quyền sở hữu
    revokeOwnership: (userId: string, labelId: string) => {
      const url = `${this.getHost(
        this.ApiHost
      )}/RoleUser/delete?userid=${userId}&labelid=${labelId}`;
      return HttpClient.delete(url);
    },
  };
  public Patch = {
    // Update document display name
    updateDocumentDisplayName: (id: string, displayName: string) => {
      const url = `${this.getHost(this.ApiHost)}/Document`;
      const body = { id, displayName };
      return HttpClient.patch(url, body);
    },
  };
}

export const UploadFileService = new UploadApiController();
